import Dexie, { type EntityTable } from 'dexie';

export interface SyncQueueItem {
    id?: number;
    url: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    payload?: string;
    entityType: string; 
    entityId: string;
    eventId?: string;
    createdAt: string;
    retryCount: number;
    lastError?: string;
    status: 'pending' | 'syncing' | 'failed' | 'completed';
}

export interface CachedEvent {
    id: string;
    name: string;
    cachedAt: string;
    lastSyncAt?: string;
    isFullyCached: boolean;
}

class SyncQueueDb extends Dexie {
    syncQueue!: EntityTable<SyncQueueItem, 'id'>;
    cachedEvents!: EntityTable<CachedEvent, 'id'>;

    constructor() {
        super('syncQueueDb');
        this.version(1).stores({
            syncQueue: '++id, url, method, entityType, entityId, eventId, status, createdAt',
            cachedEvents: 'id, name, cachedAt, isFullyCached'
        });
    }
}

export const syncQueueDb = new SyncQueueDb();

syncQueueDb.open().catch((err) => {
    console.error('[SyncQueue] Failed to open sync queue db:', err);
});

export async function addToSyncQueue(
    url: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    payload: any,
    entityType: string,
    entityId: string,
    eventId?: string
): Promise<void> {
    try {
        await syncQueueDb.syncQueue.add({
            url,
            method,
            payload: payload ? JSON.stringify(payload) : undefined,
            entityType,
            entityId,
            eventId,
            createdAt: new Date().toISOString(),
            retryCount: 0,
            status: 'pending'
        });
        console.log(`[SyncQueue] Added ${method} ${entityType}/${entityId} to sync queue`);
    } catch (error) {
        console.error('[SyncQueue] Failed to add to sync queue:', error);
    }
}

export async function getPendingSyncItems(): Promise<SyncQueueItem[]> {
    return syncQueueDb.syncQueue
        .where('status')
        .anyOf(['pending', 'failed'])
        .toArray();
}

export async function getPendingSyncCount(): Promise<number> {
    return syncQueueDb.syncQueue
        .where('status')
        .anyOf(['pending', 'failed'])
        .count();
}

export async function getPendingSyncItemsByEvent(eventId: string): Promise<SyncQueueItem[]> {
    return syncQueueDb.syncQueue
        .where('eventId')
        .equals(eventId)
        .and(item => item.status === 'pending' || item.status === 'failed')
        .toArray();
}

export async function updateSyncItemStatus(
    id: number,
    status: SyncQueueItem['status'],
    error?: string
): Promise<void> {
    const updates: Partial<SyncQueueItem> = { status };
    if (error) {
        updates.lastError = error;
    }
    if (status === 'failed') {
        const item = await syncQueueDb.syncQueue.get(id);
        if (item) {
            updates.retryCount = (item.retryCount || 0) + 1;
        }
    }
    await syncQueueDb.syncQueue.update(id, updates);
}

export async function clearCompletedSyncItems(): Promise<void> {
    await syncQueueDb.syncQueue
        .where('status')
        .equals('completed')
        .delete();
}

export async function markEventAsCached(eventId: string, eventName: string, isFullyCached: boolean = true): Promise<void> {
    await syncQueueDb.cachedEvents.put({
        id: eventId,
        name: eventName,
        cachedAt: new Date().toISOString(),
        isFullyCached
    });
}

export async function getCachedEvents(): Promise<CachedEvent[]> {
    return syncQueueDb.cachedEvents.toArray();
}

export async function isEventCached(eventId: string): Promise<boolean> {
    const cached = await syncQueueDb.cachedEvents.get(eventId);
    return cached?.isFullyCached ?? false;
}

export async function updateEventSyncTime(eventId: string): Promise<void> {
    const cached = await syncQueueDb.cachedEvents.get(eventId);
    if (cached) {
        await syncQueueDb.cachedEvents.update(eventId, {
            lastSyncAt: new Date().toISOString()
        });
    }
}

export async function removeCachedEvent(eventId: string): Promise<void> {
    await syncQueueDb.cachedEvents.delete(eventId);
}

export { syncQueueDb as default };
